import useLanguageTree from '~/commons/language'
import { reactive, watch, onUnmounted, computed, isRef, ref } from '@vue/composition-api'

let {t} = useLanguageTree()
t = capitalizeT(t)
export default function useInPlaceValidation (validationFields = {}, validationCallback, validating = ref(true)) {
  Object.keys(validationFields).forEach(key => {
    const field = validationFields[key]
    field.name = field.name || key
    field.rules = getRules(field.rules, field.name, field.rawName, validating)
    field.errors = []
    field.isValid = computed(() => !field.errors || field.errors.length === 0)
    field.computedValue = computed(() => {
      const aRef = isRef(field.value)
      const val = aRef ? field.value.value : field.value
      if (field.nestedValKey && typeof field.value === 'object') {
        if (aRef && typeof field.value.value[field.nestedValKey] !== 'undefined') {
          return field.value.value[field.nestedValKey]
        }
        return typeof field.value[field.nestedValKey] !== 'undefined'
          ? field.value[field.nestedValKey]
          : val
      }
      return val
    })
    field.stopWatch = watch(field.computedValue, async (value) => {
      const errors = await validate(value, field.rules).then((errors) => errors)
      field.errors.splice(0, field.errors.length, ...errors)
      for (let i = 0; i < (field.relatedTo || []).length; i++) {
        const key = field.relatedTo[i].key
        const condition = fields && field.relatedTo[i].condition ? field.relatedTo[i].condition(value, fields[key].computedValue) : true
        if (condition) {
          const err = await validate(fields[key].computedValue, fields[key].rules)
            .then((errors) => errors)
          fields[key].errors.splice(0, fields[key].errors.length, ...err)
          if (typeof field.relatedTo[i].callback === 'function') {
            field.relatedTo[i].callback(err)
          }
        }
      }
    }, { immediate: true, deep: true })
    validationFields[key] = field
  })

  const fields = reactive(validationFields)
  const isValid = computed(() => {
    return Object.values(fields).every(field => field.isValid)
  })
  function setValidationCallback (callback) {
    validationCallback = callback
  }
  const stopWatch = watch(isValid, (value) => {
    if (typeof validationCallback !== 'function') {
      if (validationCallback) console.debug('Validation callback is not a function')
      return
    }
    validationCallback(value)
  }, {
    immediate: true
  })
  onUnmounted(() => {
    Object.values(fields).forEach(field => {
      field.stopWatch()
    })
    stopWatch()
  })
  function getRules (paramRules = [], name = '', rawName = false, validating = ref(true)) {
    const rules = []
    for (let i = 0; i < paramRules.length; i++) {
      if (typeof paramRules[i] === 'function') {
        rules.push(paramRules[i](t))
        continue
      }
      const split = paramRules[i].split(':')
      switch (split[0]) {
        case 'numeric':
          rules.push(v => !validating.value || !v || /^[0-9]+$/.test(v) || t('validation.numeric'))
          break
        case 'required':
          rules.push(v => !validating.value || (Array.isArray(v) ? v.length > 0 : !!v) || t('validation.required', { field: toLower(t(name), rawName) }))
          break
        case 'password': {
          let charSets = []
          if (split.length > 1) {
            charSets = split[1].split(',')
          }
          const validationMessage = getKeyFromCharSets(charSets)
          const regex = buildPasswordRegex(charSets)
          rules.push(v => !validating.value || !v || regex.test(v) || t(validationMessage))
          break
        }
        case 'minLength':
          rules.push(v => !validating.value || (!v || v.length >= split[1]) || t('validation.minLength', { field: toLower(t(name), rawName), val: split[1] }))
          break
        case 'maxLength':
          rules.push(v => !validating.value || (!v || v.length <= split[1]) || t('validation.maxLength', { field: toLower(t(name), rawName), val: split[1] }))
          break
        case 'regex':
          rules.push(v => !validating.value || ((Array.isArray(v) && v.length === 0) || !v) || (new RegExp(split[1]).test(v)) || t((split[2] || 'validation.regex'), { field: toLower(t(name), rawName) }))
          break
        case 'json':
          rules.push(v => {
            if (!validating.value || !v) {
              return true
            }
            let validJSON = false
            try {
              const o = JSON.parse(v)
              if (o && typeof o === 'object') {
                validJSON = true
              }
            } catch (e) { }
            return validJSON || t('validation.malformedJSON', { field: toLower(t(name), rawName) })
          })
          break
        case 'email':
          rules.push(
            // eslint-disable-next-line no-useless-escape,no-control-regex
            v => !validating.value || !v || /(?:[\u00A0-\uD7FF\uE000-\uFFFF-a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[\u00A0-\uD7FF\uE000-\uFFFF-a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[\u00A0-\uD7FF\uE000-\uFFFF-a-z0-9](?:[\u00A0-\uD7FF\uE000-\uFFFF-a-z0-9-]*[\u00A0-\uD7FF\uE000-\uFFFF-a-z0-9])?\.)+[\u00A0-\uD7FF\uE000-\uFFFF-a-z0-9](?:[\u00A0-\uD7FF\uE000-\uFFFF-a-z0-9-]*[\u00A0-\uD7FF\uE000-\uFFFF-a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/.test(v.toLowerCase()) || t('validation.email'))
          break
      }
    }
    return rules
  }

  async function validate (value, rules = [], handlers = []) {
    const errors = []
    for (const handler of handlers) {
      const response = await handler(value)
      if (!response.valid) {
        const err = t(response.message, response.args)
        errors.push(err)
        return errors
      }
    }
    for (const rule of rules) {
      const ruleResult = rule(value)
      if (typeof ruleResult !== 'boolean' || !ruleResult) {
        errors.push(ruleResult)
        return errors
      }
    }
    return errors
  }

  async function validateField (field, rules, handlers) {
    const errors = await validate(fields[field].computedValue, rules, handlers)
    fields[field].errors = errors
    return errors
  }
  function validateAll () {
    const promises = []
    for (const field in fields) {
      promises.push(validateField(field, fields[field].rules, fields[field].handlers))
    }
    return Promise.all(promises).then((errors) => {
      errors = errors.flat()
      if (errors.length > 0) {
        throw errors
      }
      return true
    })
  }
  function clearValidation () {
    for (const field in fields) {
      fields[field].errors.splice(0, fields[field].errors.length)
    }
  }

  function setRules (key, rules) {
    if (!fields[key]) {
      throw new Error(`Field ${key} does not exist`)
    }
    const field = fields[key]
    fields[key].rules = getRules(rules, field.name, field.rawName, validating)
  }

  return {
    getRules,
    setRules,
    validate,
    fields,
    isValid,
    validateAll,
    validateField,
    clearValidation,
    setValidationCallback
  }
}

function buildPasswordRegex (charSets = []) {
  const defaultPasswordRegex = /^(?=.*[A-ZÀ-ÖØ-Þ])(?=.*[a-zà-öø-ÿ])(?=.*[!¡@#~$%^&*()_+\-=[\]{};':"\\|,.<>/¿?])(?=.*[0-9]).+$/
  if (!charSets || charSets.length === 0) {
    return defaultPasswordRegex
  }
  const lookaheads = []

  for (const charSet of charSets) {
    switch (charSet) {
      case 'upper':
        lookaheads.push('(?=.*[A-ZÀ-ÖØ-Þ])')
        break
      case 'lower':
        lookaheads.push('(?=.*[a-zà-öø-ÿ])')
        break
      case 'special':
        lookaheads.push("(?=.*[!¡@~#$%^&*()_+\\-=\\[\\]{};':\"\\\\|,.<>\\/¿?])")
        break
      case 'numeric':
        lookaheads.push('(?=.*[0-9])')
        break
      default:
        return defaultPasswordRegex
    }
  }
  return new RegExp(`^${lookaheads.join('')}.+$`)
}

function getKeyFromCharSets (charSets) {
  const prefix = 'validation.password.'
  if (!charSets || charSets.length === 0) {
    return prefix + 'lowerNumericSpecialUpper'
  }
  return prefix + charSets
    .sort()
    .map((charSet, index) => {
      if (index === 0) {
        return charSet
      }
      return charSet.charAt(0).toUpperCase() + charSet.slice(1)
    })
    .join('')
}

function capitalizeT (t) {
  return function (key, ...params) {
    const translated = t(key, ...params)
    return translated.charAt(0).toUpperCase() + translated.slice(1)
  }
}

function toLower (val, isRaw) {
  if (isRaw) return val
  return val.toLowerCase()
}



// WEBPACK FOOTER //
// ./src/commons/validation.js